import React from 'react';
import { Link } from 'react-router-dom';
import Countdown from 'react-countdown';
import { MapPin, Heart, Eye, Package, Gavel, ClipboardPenLine } from 'lucide-react';
import i18n from '../../../../i18n';
import { toggleWishlist } from '../../../../models/auctions';
import { formatCurrency } from '../../../../state/utils';
import { CustomEnroll } from '../../../helpers/customEnroll';     // ✅ restored
import { VisitNow } from '../../helpers/visitNow';                // ✅ restored
import '../../../../styles/components/auction-card-real-estate.scss';
import { OmrRiyal } from '../../../common/icons';

const is_logged = localStorage.getItem('is_logged_in') ? true : false;

const CountdownStrip = ({ days, hours, minutes, seconds, isExpired, stripColor }) => {
    const label = isExpired
        ? (i18n.t('bid_time_ends') || 'Bid time ends!!!')
        : (i18n.t('ends_after') || 'End After');
    const units = [
        { val: String(days).padStart(2, '0'), lbl: i18n.t('lbl_day') || 'Day', zeroed: days === 0 },
        { val: String(hours).padStart(2, '0'), lbl: i18n.t('lbl_hour') || 'Hour', zeroed: days === 0 && hours === 0 },
        { val: String(minutes).padStart(2, '0'), lbl: i18n.t('lbl_minute') || 'Minute', zeroed: days === 0 && hours === 0 && minutes === 0 },
        { val: String(seconds).padStart(2, '0'), lbl: i18n.t('lbl_second') || 'Second', zeroed: days === 0 && hours === 0 && minutes === 0 && seconds === 0 },
    ];
    return (
        <div className="re-auc-countdown">
            <div className="re-auc-countdown__inner" style={{ backgroundColor: stripColor }}>
                <span className="re-auc-countdown__label">{label}</span>
                <div className="re-auc-countdown__units">
                    {units.map((u, i) => (
                        <div key={i} className={`re-auc-countdown__unit ${u.zeroed ? 're-auc-countdown__unit--zeroed' : ''}`}>
                            <span className="re-auc-countdown__num">{u.val}</span>
                            <span className="re-auc-countdown__text">{u.lbl}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default class AuctionGroupCardNew extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            wishlist: props.group.wishlisted || false,
        };
        this._timer = null;
        this.incrementTime = this.incrementTime.bind(this);
    }

    componentDidMount() {
        if (this.props.group) {
            this._timer = setInterval(this.incrementTime, 1000);
        }
    }

    componentWillUnmount() {
        if (this._timer) clearInterval(this._timer);
    }

    incrementTime() {
        const { group } = this.props;
        if (!group) return;
        const dt = new Date(group.server_time);
        dt.setSeconds(dt.getSeconds() + 1);
        group.server_time = dt;
        this.setState({});
    }

    async toggleWish() {
        if (!is_logged) return;
        const next = !this.state.wishlist;
        await toggleWishlist({ group_id: this.props.group.id, wishlist: next });
        this.setState({ wishlist: next });
    }

    createDate(dt) {
        return dt ? new Date(dt.replace(/-/g, '/')) : null;
    }

    formatDateParts(dateStr) {
        if (!dateStr) return { date: '', time: '', day: '' };
        const d = new Date(dateStr.replace(/-/g, '/'));
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return { date, time: `${String(h12).padStart(2, '0')}:${m} ${ampm}`, day: dayNames[d.getDay()] };
    }

    render() {
        const { group } = this.props;
        const lng = localStorage.getItem('I18N_LANGUAGE');
        const { wishlist } = this.state;

        // ✅ Use status_dis like old GroupCard
        const isExpired = group.is_bid_time_ends || group.status_dis !== 'A';
        const now = new Date();
        const isUpcoming = group.start_date && new Date(group.start_date) > now;

        // ✅ Use status_label like old GroupCard
        let stripColor = '#0c8da2';
        let statusClass = 're-auc-badge--active';
        let statusLabel = i18n.t('active') || 'Active';
        const statusAr = group.status_label;
        if (statusAr?.status) {
            const statusClasses = {
                E: 're-auc-badge--expired',
                A: 're-auc-badge--active',
                U: 're-auc-badge--upcoming',
            };
            statusClass = statusClasses[statusAr.status] || 're-auc-badge--active';
            statusLabel = lng === 'ar' ? statusAr.ar : statusAr.en;
            if (statusAr.status === 'E') stripColor = '#d93025';
            else if (statusAr.status === 'U') stripColor = '#bfc531';
        }

        const countdownTarget = isUpcoming
            ? new Date(this.createDate(group.start_date)).getTime()
            : new Date(this.createDate(group.end_date)).getTime();

        // ✅ group.group_image is a string, not an array
        const imgSrc = group.group_image
            || process.env.PUBLIC_URL + '/assets/images/placeholder.jpg';

        const startParts = this.formatDateParts(group.start_date);
        const endParts = this.formatDateParts(group.end_date);
        const location = group.location || '';

        // ✅ correct field names
        const title = lng === 'ar' ? (group.group_name_ar || group.group_name) : (group.group_name || group.group_name_ar);
        const category = lng === 'ar' ? (group.category_name_ar || group.category_name) : group.category_name;
        const auctionCount = group.auctions_count || 0;

        // ✅ enroll conditions from old GroupCard
        const canEnrollDirect =
            this.props.can_bid &&
            group.status_dis === 'A' &&
            !group.is_enrolled &&
            group.first_auction_id &&
            group.is_grouped === 1 &&
            group.is_grouped_enroll === 1;

        const canCustomEnroll =
            this.props.can_bid &&
            group.status_dis === 'A' &&
            group.first_auction_id &&
            group.is_grouped === 1 &&
            group.is_grouped_enroll === 0;

        // ✅ group.group is the navigation ID (not group.id)
        const groupNavId = group.group;

        let orgImage = '';
        if (
            group &&
            group.organizationDetails &&
            group.organizationDetails.file_organization_image_full
        ) {
            orgImage = group.organizationDetails.file_organization_image_full;
        }

        return (

            <div >
                <div className="re-auc-card">

                    <span className={`re-auc-badge ${statusClass}`}>{statusLabel}</span>
                    {/* IMAGE */}
                    <Link to={`/grouped-auctions/${groupNavId}?type=${this.props.type || ''}`}>
                        <div className="re-auc-img-wrap">
                            <img src={imgSrc} alt={title} className="re-auc-img" />


                            {/* Auctions count — top right */}
                            {/* <span
                                className="re-auc-badge"
                                style={{
                                    left: 'auto', right: '14px',
                                    background: 'rgba(0,0,0,0.52)',
                                    backdropFilter: 'blur(6px)',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                <Package size={10} />
                                {auctionCount} {i18n.t('auctions') || 'Auctions'}
                            </span> */}

                            {location && (
                                <div className="re-auc-location">
                                    <MapPin size={10} />
                                    <span>{location}</span>
                                </div>
                            )}

                            <button
                                onClick={() => this.toggleWish()}
                                className={`re-auc-wishlist ${wishlist ? 're-auc-wishlist--active' : ''}`}
                            >
                                <Heart size={14} fill={wishlist ? '#e74c3c' : 'none'} color={wishlist ? '#e74c3c' : '#ccc'} />
                            </button>
                            {!isExpired &&
                                <Countdown
                                    date={countdownTarget}
                                    now={() => new Date(group.server_time)}
                                    onComplete={() => this.setState({})}
                                    renderer={(props) => (
                                        <CountdownStrip {...props} isExpired={isExpired} stripColor={stripColor} />
                                    )}
                                />}
                        </div>
                    </Link>

                    {/* BODY */}
                    <div className="re-auc-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                            <div>
                                <div className="re-auc-amount__label">MZAD-{group.id}</div>

                                {<div className="re-auc-category">{i18n.t('group_auction')}</div>}

                                <h3 className="re-auc-title">{title}</h3>
                            </div>

                            <div className="re-auc-topline__side">
                                {orgImage && (
                                    <img src={orgImage} alt="" className="re-auc-topline__org" />
                                )}
                                {group.bid_count !== 0 &&
                                    <div className="re-auc-topline__bids">
                                        <Gavel size={13} />
                                        <span>{group.bid_count >= 1000
                                            ? `${(group.bid_count / 1000).toFixed(1)}k`
                                            : group.bid_count}
                                        </span>
                                    </div>}
                            </div>

                        </div>

                        <div className="re-auc-amounts">



                            <div className="re-auc-amount">
                                {/* ✅ group.start_amount not starting_price */}
                                <span className="re-auc-amount__label">{i18n.t('total_auctions')}</span>
                                <span className="re-auc-amount__value re-auc-amount__value--start">
                                    {auctionCount} &nbsp;
                                    <Package size={22} />
                                    {/* <OmrRiyal width={22} height={22} /> */}
                                </span>
                            </div>
                            <div className="re-auc-amount">
                                <span className="re-auc-amount__label">{i18n.t('start_amount')}</span>
                                <span className="re-auc-amount__value re-auc-amount__value--current">
                                    {formatCurrency(group.start_amount)} &nbsp;
                                    <OmrRiyal width={22} height={22} />
                                </span>
                            </div>
                            {group.guarantee_amount && <div className="re-auc-amount">
                                <span className="re-auc-amount__label">{i18n.t('guarantee_amount')}</span>
                                <span className="re-auc-amount__value">{formatCurrency(group.guarantee_amount)} &nbsp;
                                    <OmrRiyal width={22} height={22} /></span>
                            </div>}
                            <div className="re-auc-amount">
                                <span className="re-auc-amount__label">{i18n.t('vat')}</span>
                                <span className="re-auc-amount__value">{group.vat || '0'} %</span>
                            </div>
                        </div>

                        {/* ✅ VisitNow — restored from old GroupCard */}
                        {Number(group.visit_amount) > 0 && (
                            <div style={{ padding: '4px 0 8px', fontSize: 12, color: '#6b7280' }}>
                                <strong>{i18n.t('visit_amount')}: {formatCurrency(group.visit_amount)} {i18n.t('omr')}</strong>
                                {group.is_visit_active && (
                                    <VisitNow
                                        amount={group.visit_amount}
                                        type="sitevisit"
                                        id={null}
                                        gid={group.group}
                                        is_group={true}
                                        main_class="re-auc-btn re-auc-btn--details"
                                        style={{ marginLeft: 8 }}
                                    />
                                )}
                            </div>
                        )}

                        <div className="re-auc-dates">
                            <div className="re-auc-dates__col">
                                <span className="re-auc-dates__label">{i18n.t('current_amount') || 'Bids'}</span>
                                <span className="re-auc-dates__pill">{group.current_amount === 0 ? formatCurrency(group.start_amount) : formatCurrency(group.current_amount)}  <OmrRiyal width={22} height={22} /></span>


                            </div>
                            <div className="re-auc-dates__col">
                                <span className="re-auc-dates__label">{i18n.t('start_date')}</span>
                                <span className="re-auc-dates__date">{startParts.date}</span>
                                <span className="re-auc-dates__time">{i18n.t(startParts.day)}</span>
                                <span className="re-auc-dates__time">{startParts.time}</span>
                            </div>
                            <div className="re-auc-dates__col">
                                <span className="re-auc-dates__label">{i18n.t('end_date')}</span>
                                <span className="re-auc-dates__date">{endParts.date}</span>
                                <span className="re-auc-dates__time">{i18n.t(endParts.day)}</span>
                                <span className="re-auc-dates__time">{endParts.time}</span>
                            </div>
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div className="re-auc-footer">
                        {/* ✅ correct route: /grouped-auctions/ + group.group */}
                        <Link
                            to={`/grouped-auctions/${groupNavId}?type=${this.props.type || ''}`}
                            className="re-auc-btn re-auc-btn--details"
                        >
                            <Eye size={13} />
                            {i18n.t('list_auctions') || 'View Auctions'}
                        </Link>

                        {/* ✅ direct enroll via first_auction_id */}

                        {/* <Link
                            to={!is_logged ? '/login' : '/enroll/' + group.first_auction_id}
                            className="re-auc-btn re-auc-btn--enroll"
                        >
                            {i18n.t('enroll') || 'Enroll'}
                        </Link> */}


                        {canEnrollDirect && 
                            (
                                
                                    <Link
                                        to={!is_logged ? '/login' : '/enroll/' + group.first_auction_id}
                                        className="re-auc-btn re-auc-btn--enroll"
                                    >
                                        <ClipboardPenLine size={14}/>
                                        &nbsp;
                                        <span className="d-none-mob px-1">{i18n.t('enroll')}</span>
                                    </Link>
                            )}


                        {/* ✅ custom enroll when is_grouped_enroll === 0 */}
                        {canCustomEnroll && (
                            <CustomEnroll
                                group={group.group}
                                className="re-auc-btn re-auc-btn--enroll"
                            />
                        )}
                    </div>
                    {/* <div className='re-auc-btn re-auc-btn--enroll'>
                        {i18n.t('final_approval_lbl')}
                    </div> */}
                </div>
            </div>
        );
    }
}